import { auth, db } from '../firebase'; 
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  sendEmailVerification 
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

export const useFirebaseAuth = () => {
  const signUpWithEmail = async (name, phone, email, password) => {
    try {
      const [firstName, ...lastNameArr] = name.split(' ');
      const lastName = lastNameArr.join(' ');

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Send email verification
      await sendEmailVerification(user);
      
      const userDocRef = doc(db, 'users', user.uid);
      // create user root doc (without embedded subscription)
      await setDoc(userDocRef, {
        UserID: user.uid,
        isOnboardingDone: true
      });
      // create Subscription subcollection document
      const subscriptionRef = doc(db, 'users', user.uid, 'Subscription', user.uid);
      await setDoc(subscriptionRef, {
        plan: 'Free',
        startFrom: new Date().toISOString(),
        validity: 1
      });

      const userProfileRef = doc(db, 'users', user.uid, 'UserProfile', user.uid);
      // create profile without embedded subscription (subscription lives in subcollection)
      await setDoc(userProfileRef, {
        firstName: firstName || '',
        lastName: lastName || '',
        phoneNumber: phone,
        emailAddress: user.email,
        displayName: name,
        createdAt: new Date().toISOString()
      });

      return userCredential;
    } catch (error) {
      console.error("Error in signUpWithEmail:", error);
      throw error;
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if email is verified
      if (!user.emailVerified) {
        const error = new Error('Email not verified');
        error.code = 'auth/email-not-verified';
        error.email = user.email;
        throw error;
      }
      
      return userCredential;
    } catch (error) {
      console.error("Error in signInWithEmail:", error);
      throw error;
    }
  };
  
  const resendVerificationEmail = async (email = null, password = null) => {
    try {
      let currentUser = auth.currentUser;
      
      // If not currently logged in but email/password provided, sign in silently first
      if (!currentUser && email && password) {
        try {
          const userCred = await signInWithEmailAndPassword(auth, email, password);
          currentUser = userCred.user;
        } catch (error) {
          console.error("Error signing in to send verification:", error);
          const verificationError = new Error('Failed to authenticate for verification email');
          verificationError.code = 'auth/verification-sign-in-failed';
          throw verificationError;
        }
      }
      
      if (currentUser) {
        await sendEmailVerification(currentUser);
        return { success: true };
      }
      
      return { success: false, error: 'No user found' };
    } catch (error) {
      console.error("Error in resendVerificationEmail:", error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userDocRef = doc(db, 'users', user.uid);
      const subscriptionRef = doc(db, 'users', user.uid, 'Subscription', user.uid);
      const userProfileRef = doc(db, 'users', user.uid, 'UserProfile', user.uid);
      const [userDoc, subscriptionDoc, userProfileDoc] = await Promise.all([
        getDoc(userDocRef),
        getDoc(subscriptionRef),
        getDoc(userProfileRef)
      ]);

      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          UserID: user.uid,
          isOnboardingDone: true
        });
      }

      if (!subscriptionDoc.exists()) {
        await setDoc(subscriptionRef, {
          plan: 'Free',
          startFrom: new Date().toISOString(),
          validity: 1
        });
      }

      const nameParts = (user.displayName || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      if (!userProfileDoc.exists()) {
        await setDoc(userProfileRef, {
          firstName,
          lastName,
          displayName: user.displayName || 'User',
          phoneNumber: user.phoneNumber || '',
          emailAddress: user.email,
          createdAt: new Date().toISOString(),
          profilePhoto: user.photoURL
        });
      }

      return result;
    } catch (error) {
      console.error("Error in signInWithGoogle:", error);
      throw error;
    }
  };

  return { 
    signUpWithEmail, 
    signInWithEmail, 
    signInWithGoogle,
    resendVerificationEmail
  };
};